import { useEffect, useRef, useState, useCallback, type ReactNode } from 'react'
import { OfflineCameraIcon } from './OfflineCameraIcon'
import { describeHolder, type HeldBy } from '../useSpeaker'
import { trimPlan, type Range } from '../liveBuffer'

interface Props {
  siteId: string
  siteName: string
  camera: string
  /** True when the site reports this camera is sending no video. */
  offline: boolean
  onClose: () => void
  onSpeak: () => void
  // Someone else holding the camera's speaker; live talk would be refused.
  heldBy?: HeldBy | null
  // Live talk ended or was refused, so who holds the speaker has changed.
  onTalkEnded?: () => void
  // False when the server has no voice service: live talk still works, but
  // typed messages would only fail.
  speechAvailable?: boolean
  /**
   * Where it sits: a page of its own, or inside an incident — in the clip's
   * corner, large, or with the picture hidden. Inside an incident it drops the
   * page around it but keeps its controls, so a dispatcher can talk while the
   * clip plays. Every layout keeps the same picture, so moving between them
   * never starts a new stream or ends a talk (#287).
   */
  layout?: StreamLayout
  /** Drawn over the picture: an incident's corner buttons. */
  overlay?: ReactNode
  /** Where the corner sits, measured by the incident against its clip. */
  cornerTop?: number
  /** The page's back button's label: the view it returns to. */
  backLabel?: string
  /** In the page's header: a way to another camera, from App. */
  picker?: ReactNode
  /** Shown under the controls on the page: the camera's latest incident, from App. */
  children?: ReactNode
}

export type StreamLayout = 'page' | 'corner' | 'large' | 'hidden'

const pictureBox: Record<StreamLayout, string> = {
  page: 'relative w-full aspect-video max-h-[62vh] rounded-lg overflow-hidden border border-zinc-800 bg-black',
  large: 'relative w-full aspect-video max-h-[60vh] rounded-lg overflow-hidden border border-zinc-800 bg-black',
  corner: 'absolute right-3 top-3 z-10 w-[30%] max-w-80 aspect-video rounded-md overflow-hidden border-2 border-white/85 shadow-[0_8px_24px_rgb(0_0_0/0.55)] bg-black',
  hidden: 'relative',
}

const CODECS = [
  'avc1.640029',
  'avc1.64002A',
  'avc1.640033',
  'hvc1.1.6.L153.B0',
  'mp4a.40.2',
  'mp4a.40.5',
  'flac',
  'opus',
]

const STALL_MS = 10_000
const MAX_BACKOFF_MS = 30_000
const MIC_GAIN = 8 // built-in mics are quiet at G.711 source level; boost before quantizing

// MicAnswer is the server's answer to an unmute.
type MicAnswer = { type: string; reason?: string }

export function StreamPanel({ siteId, siteName, camera, offline, onClose, onSpeak, heldBy = null, onTalkEnded, speechAvailable = true, layout = 'page', overlay, cornerTop, backLabel = 'Sites', picker, children }: Props) {
  const page = layout === 'page'
  // Opening a camera as a page takes focus to its name: the tile or card it was
  // opened from has gone, and focus fell to the page (#287). Only on opening —
  // it stays where the dispatcher put it as an incident comes and goes.
  const heading = useRef<HTMLHeadingElement>(null)
  useEffect(() => { heading.current?.focus() }, [])
  const watching = layout !== 'hidden'
  const videoRef = useRef<HTMLVideoElement>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const micWsRef = useRef<WebSocket | null>(null)
  const micStreamRef = useRef<MediaStream | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const [muted, setMuted] = useState(true)
  const [micOn, setMicOn] = useState(false)
  const [retryKey, setRetryKey] = useState(0)
  const [status, setStatus] = useState<'connecting' | 'playing' | 'stalled' | 'reconnecting'>('connecting')
  const [hasAudio, setHasAudio] = useState(false)
  const [hasMic, setHasMic] = useState(false)
  // The IP speakers live talk could not reach, as the server reports them.
  const [missed, setMissed] = useState<string[]>([])
  const [audioError, setAudioError] = useState<string | null>(null)
  const micCodecRef = useRef<{ name: string; sampleRate: number } | null>(null)
  const retryCountRef = useRef(0)
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimerRef.current !== null) return
    const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** retryCountRef.current)
    retryCountRef.current += 1
    setStatus('reconnecting')
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null
      setRetryKey(k => k + 1)
    }, delay)
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    const onVolumeChange = () => setMuted(video.muted)
    video.addEventListener('volumechange', onVolumeChange)
    return () => video.removeEventListener('volumechange', onVolumeChange)
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    // No point opening a socket to a camera that is sending nothing; the
    // reconnect backoff would just hammer go2rtc behind the offline notice.
    // Nor while the picture is hidden: the panel stays so a talk carries on,
    // which is its own connection, but the video would be downloaded unseen —
    // on a phone, beside every incident's clip.
    if (offline || !watching) return

    let intentionalClose = false

    const clearStallTimer = () => {
      if (stallTimerRef.current !== null) {
        clearTimeout(stallTimerRef.current)
        stallTimerRef.current = null
      }
    }

    const onPlaying = () => {
      clearStallTimer()
      setStatus('playing')
    }

    const onWaiting = () => {
      if (stallTimerRef.current !== null) return
      setStatus('stalled')
      stallTimerRef.current = setTimeout(() => {
        stallTimerRef.current = null
        scheduleReconnect()
      }, STALL_MS)
    }

    video.addEventListener('playing', onPlaying)
    video.addEventListener('waiting', onWaiting)
    video.addEventListener('stalled', onWaiting)

    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${proto}//${window.location.host}/${siteId}/api/ws?src=${encodeURIComponent(camera)}&media=video+audio`

    const isManagedMS = 'ManagedMediaSource' in window
    const MSClass: typeof MediaSource = isManagedMS
      ? (window as unknown as { ManagedMediaSource: typeof MediaSource }).ManagedMediaSource
      : MediaSource

    let objectUrl = ''
    let sb: SourceBuffer | null = null
    const buf = new Uint8Array(8 * 1024 * 1024)
    let bufLen = 0
    let ms: MediaSource | null = null

    const ws = new WebSocket(wsUrl)
    ws.binaryType = 'arraybuffer'
    wsRef.current = ws

    ws.addEventListener('open', () => {
      ms = new MSClass()

      ms.addEventListener('sourceopen', () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl)
        const supported = CODECS.filter(codec =>
          MSClass.isTypeSupported(`video/mp4; codecs="${codec}"`)
        ).join(',')
        ws.send(JSON.stringify({ type: 'mse', value: supported }))
      }, { once: true })

      if (isManagedMS) {
        video.disableRemotePlayback = true
        video.srcObject = ms as unknown as MediaStream
      } else {
        objectUrl = URL.createObjectURL(ms)
        video.src = objectUrl
        video.srcObject = null
      }

      video.play().catch(() => {})
    })

    ws.addEventListener('close', () => {
      if (intentionalClose) return
      clearStallTimer()
      scheduleReconnect()
    })

    ws.addEventListener('message', (ev) => {
      if (typeof ev.data === 'string') {
        const msg = JSON.parse(ev.data) as { type: string; value: string }
        if (msg.type === 'mse' && ms) {
          // Detect audio from negotiated codec string
          setHasAudio(/mp4a|opus|flac/i.test(msg.value))

          // Whether the camera can be talked to, and in which codec: VMS
          // answers, since go2rtc's own stream details carry the camera's
          // password and are not passed through.
          fetch(`/api/talk?site=${encodeURIComponent(siteId)}&camera=${encodeURIComponent(camera)}`)
            .then(r => (r.ok ? r.json() : { talk: false }))
            .then((t: { talk: boolean; codec?: string; sample_rate?: number }) => {
              if (!t.talk) return
              micCodecRef.current = { name: t.codec ?? 'PCMA', sampleRate: t.sample_rate ?? 8000 }
              setHasMic(true)
            })
            .catch(() => {})

          sb = ms.addSourceBuffer(msg.value)
          sb.mode = 'segments'
          sb.addEventListener('updateend', () => {
            // A reconnect closes the source while an append is finishing.
            if (!sb || sb.updating || ms?.readyState !== 'open') return
            if (bufLen > 0) {
              // Trimmed on this append's own updateend: remove() would throw now.
              try { sb.appendBuffer(buf.slice(0, bufLen)); bufLen = 0 } catch { bufLen = 0 }
              return
            }
            const b = sb.buffered
            const ranges = Array.from({ length: b.length }, (_, i): Range => [b.start(i), b.end(i)])
            const { remove, seekTo } = trimPlan(ranges, video.currentTime)
            if (remove) {
              // Trim races the decoder; a rejected call just means try again next append.
              try { sb.remove(remove[0], remove[1]); ms.setLiveSeekableRange(remove[1], ranges[ranges.length - 1][1]) } catch { /* retried on next updateend */ }
            }
            if (seekTo !== undefined) video.currentTime = seekTo
          })
        }
      } else {
        if (!sb) return
        const data = ev.data as ArrayBuffer
        if (sb.updating || bufLen > 0) {
          if (bufLen + data.byteLength > buf.length) bufLen = 0
          buf.set(new Uint8Array(data), bufLen)
          bufLen += data.byteLength
        } else {
          try { sb.appendBuffer(data) } catch { /* buffer busy; the next updateend drains it */ }
        }
      }
    })

    return () => {
      intentionalClose = true
      clearStallTimer()
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
      wsRef.current = null
      ws.close()
      video.removeEventListener('playing', onPlaying)
      video.removeEventListener('waiting', onWaiting)
      video.removeEventListener('stalled', onWaiting)
      if (objectUrl) URL.revokeObjectURL(objectUrl)
      video.src = ''
      video.srcObject = null
    }
  }, [siteId, camera, retryKey, offline, watching, scheduleReconnect])

  // Stop mic when camera changes
  useEffect(() => {
    return () => {
      audioCtxRef.current?.close()
      audioCtxRef.current = null
      micStreamRef.current?.getTracks().forEach(t => t.stop())
      micStreamRef.current = null
      micWsRef.current?.close()
      micWsRef.current = null
      setMicOn(false)
    }
  }, [siteId, camera])

  const toggleSpeaker = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    video.muted = !video.muted
    setMuted(video.muted)
  }, [])

  const startAudioCapture = useCallback(async (micWs: WebSocket, sampleRate: number) => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
    micStreamRef.current = stream

    const audioCtx = new AudioContext({ sampleRate })
    audioCtxRef.current = audioCtx

    const workletSrc = `
class PCMProcessor extends AudioWorkletProcessor {
  process(inputs, outputs) {
    const ch = inputs[0]?.[0]
    if (ch) this.port.postMessage(ch.buffer, [ch.buffer])
    const out = outputs[0]?.[0]
    if (out) out.fill(0)
    return true
  }
}
registerProcessor('pcm-processor', PCMProcessor)`
    const blob = new Blob([workletSrc], { type: 'application/javascript' })
    const workletUrl = URL.createObjectURL(blob)
    await audioCtx.audioWorklet.addModule(workletUrl)
    URL.revokeObjectURL(workletUrl)

    const source = audioCtx.createMediaStreamSource(stream)
    const gainNode = audioCtx.createGain()
    gainNode.gain.value = MIC_GAIN
    const worklet = new AudioWorkletNode(audioCtx, 'pcm-processor')
    worklet.port.onmessage = (e) => {
      if (micWs.readyState !== WebSocket.OPEN) return
      const float32 = new Float32Array(e.data as ArrayBuffer)
      const pcm16 = new Int16Array(float32.length)
      for (let i = 0; i < float32.length; i++) {
        pcm16[i] = Math.max(-32768, Math.min(32767, float32[i] * 32768))
      }
      micWs.send(pcm16.buffer)
    }
    source.connect(gainNode)
    gainNode.connect(worklet)
    worklet.connect(audioCtx.destination)
  }, [])

  const stopAudioCapture = useCallback(() => {
    audioCtxRef.current?.close()
    audioCtxRef.current = null
    micStreamRef.current?.getTracks().forEach(t => t.stop())
    micStreamRef.current = null
  }, [])

  // Missed speakers are reported as live talk opens and again once the rest
  // have had their time to connect, so a report can land while an unmute
  // waits for its answer; it is never taken for that answer.
  const micMessage = useCallback((e: MessageEvent, answer?: (a: MicAnswer) => void) => {
    if (typeof e.data !== 'string') return
    const m = JSON.parse(e.data) as MicAnswer & { speakers?: string[] }
    if (m.type === 'missed') setMissed(m.speakers ?? [])
    else answer?.(m)
  }, [])

  const toggleMic = useCallback(async () => {
    if (micOn) {
      // Stop capture but keep relay WS open — closing it would release go2rtc's
      // RTSP backchannel; re-establishing it while go2rtc's ICE timeout is still
      // running causes "wrong response on SETUP".
      stopAudioCapture()
      setMicOn(false)
      // The socket stays open, so the server is told to let the camera's
      // speaker go — otherwise a muted dispatcher holds it for as long as
      // live view is open.
      if (micWsRef.current?.readyState === WebSocket.OPEN) micWsRef.current.send('mute')
      onTalkEnded?.()
      return
    }
    try {
      // Reuse existing relay WS if still open.
      let micWs = micWsRef.current
      if (!micWs || micWs.readyState !== WebSocket.OPEN) {
        const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
        const mc = micCodecRef.current
        const codecParam = mc ? `${mc.name}/${mc.sampleRate}` : 'PCMA/8000'
        micWs = new WebSocket(
          `${proto}//${window.location.host}/api/mic?site=${siteId}&camera=${encodeURIComponent(camera)}&codec=${encodeURIComponent(codecParam)}`
        )
        micWsRef.current = micWs

        await new Promise<void>((resolve, reject) => {
          micWs!.onopen = () => resolve()
          micWs!.onerror = () => reject(new Error('mic ws failed'))
          micWs!.onclose = () => reject(new Error('mic ws closed'))
        })

        micWs.onmessage = (e) => micMessage(e)
        micWs.onclose = (event) => {
          stopAudioCapture()
          micWsRef.current = null
          setMicOn(false)
          setMissed([])
          onTalkEnded?.()
          if (event.code === 4000 || event.code === 4009) {
            setAudioError(event.reason || 'Mic unavailable — camera backchannel in use')
          }
        }
      } else {
        // Back from mute: the speaker has to be taken again, and someone may
        // have taken it meanwhile.
        const ws = micWs
        const answer = await new Promise<MicAnswer>((resolve) => {
          ws.onmessage = (e) => micMessage(e, resolve)
          ws.send('unmute')
        })
        if (answer.type === 'refused') {
          setAudioError(answer.reason ?? 'Someone else is speaking on this camera')
          onTalkEnded?.()
          return
        }
      }

      await startAudioCapture(micWs, micCodecRef.current?.sampleRate ?? 8000)
      // Guard: onclose may have fired (code 4000) while getUserMedia was pending.
      if (micWs.readyState !== WebSocket.OPEN) return
      setMicOn(true)
      setAudioError(null)
    } catch {
      stopAudioCapture()
      micWsRef.current?.close()
      micWsRef.current = null
    }
  }, [micOn, siteId, camera, startAudioCapture, stopAudioCapture, onTalkEnded, micMessage])

  const takeSnapshot = useCallback(() => {
    const video = videoRef.current
    if (!video || !video.videoWidth) return
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    canvas.toBlob(blob => {
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const stamp = new Date().toISOString().replace(/[:.]/g, '-')
      a.href = url
      a.download = `${camera}_${siteId}_${stamp}.png`
      a.click()
      URL.revokeObjectURL(url)
    }, 'image/png')
  }, [camera, siteId])

  const picture = (
    <>
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        playsInline
        muted
        controls={page || layout === 'large'}
      />
      {offline ? (
        // Say the camera is down rather than spin on a connection that
        // cannot succeed — otherwise this is indistinguishable from the app
        // being broken.
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-zinc-950">
          <OfflineCameraIcon className="w-8 h-8 text-fault" />
          <span className="text-sm font-semibold text-fault">Camera offline</span>
          <span className="text-xs text-zinc-400">{camera} is not sending video</span>
        </div>
      ) : status !== 'playing' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 pointer-events-none">
          <div aria-hidden="true" className="w-5 h-5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin motion-reduce:animate-none" />
          <span role="status" className="text-xs text-zinc-300">
            {status === 'connecting' ? 'Connecting…'
              : status === 'stalled' ? 'Stream stalled…'
              : 'Reconnecting…'}
          </span>
        </div>
      )}
    </>
  )

  // A control button: labelled, and pressed while on, rather than an icon
  // named only in a tooltip (#280).
  const tool = 'h-10 px-3.5 flex items-center justify-center gap-2 rounded-md border text-sm font-semibold transition-colors disabled:opacity-45 disabled:cursor-not-allowed'
  const toolState = (on: boolean) => on
    ? 'border-orange-400 bg-orange-500/10 text-orange-300 hover:bg-orange-500/15'
    : 'border-zinc-800 bg-zinc-900 text-zinc-100 hover:bg-zinc-800'

  return (
    // Inside an incident the panel's parts join CameraView's layout: the
    // picture where it places it, the controls under it.
    <div className={page ? 'h-full relative overflow-y-auto scrollbar-thin px-4 py-4 sm:px-6 sm:py-5 flex flex-col gap-3.5' : 'contents'}>
      {page ? <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2">
        <button type="button" onClick={onClose} className="h-9 pl-2 pr-3 flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 text-sm text-zinc-100 hover:bg-zinc-800">
          <svg aria-hidden="true" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m15 18-6-6 6-6" />
          </svg>
          {backLabel}
        </button>
        <div className="min-w-0 flex flex-col gap-0.5">
          <h1 ref={heading} tabIndex={-1} className="text-2xl font-bold tracking-tight text-zinc-100 truncate">{camera}</h1>
          <p className="text-sm text-zinc-400 truncate">{siteName}</p>
        </div>
        {picker}
        <div className="flex-1" />
        {!offline && (
          <span className={`h-7 px-2.5 inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-zinc-900 text-[13px] font-semibold ${status === 'playing' ? 'text-live' : 'text-zinc-400'}`}>
            <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-current" />
            {status === 'playing' ? 'Live' : 'Connecting'}
          </span>
        )}
      </div> : null}

      {/* Hidden rather than removed, so the stream and any talk carry on. */}
      <div className={pictureBox[layout]} hidden={layout === 'hidden'} style={layout === 'corner' && cornerTop !== undefined ? { top: cornerTop } : undefined}>
        {picture}
        {overlay}
      </div>

      {audioError && (
        <div role="alert" className="flex items-center gap-2 px-3 py-2 rounded-md bg-fault/10 border border-fault/40 text-sm text-fault">
          <svg aria-hidden="true" className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <span className="flex-1">{audioError}</span>
          <button type="button" aria-label="Dismiss" onClick={() => setAudioError(null)} className="w-7 h-7 max-sm:w-11 max-sm:h-11 flex items-center justify-center rounded-md hover:bg-fault/15">
            <svg aria-hidden="true" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Two by two on a phone, where a wrapped row left one button alone. A
          group, not a toolbar: a toolbar promises arrow keys between buttons. */}
      <div role="group" aria-label="Camera" className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
        <button type="button" onClick={takeSnapshot} className={`${tool} ${toolState(false)}`}>
          <svg aria-hidden="true" className="w-[17px] h-[17px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h3l2-3h8l2 3h3v12H3zM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
          </svg>
          Snapshot
        </button>
        {hasAudio && (
          <button type="button" aria-pressed={!muted} onClick={toggleSpeaker} className={`${tool} ${toolState(!muted)}`}>
            <svg aria-hidden="true" className="w-[17px] h-[17px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d={muted ? 'M11 5 6 9H2v6h4l5 4zM23 9l-6 6M17 9l6 6' : 'M11 5 6 9H2v6h4l5 4zM15.5 8.5a5 5 0 0 1 0 7M19 5a10 10 0 0 1 0 14'} />
            </svg>
            {muted ? 'Listen' : 'Listening'}
          </button>
        )}
        {hasMic && (
          <button
            type="button"
            aria-pressed={micOn}
            onClick={toggleMic}
            disabled={!micOn && heldBy !== null}
            title={micOn ? 'Mic on — click to mute' : heldBy ? describeHolder(heldBy) : 'Mic off — click to enable'}
            className={`${tool} ${toolState(micOn)}`}
          >
            <svg aria-hidden="true" className="w-[17px] h-[17px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zM19 10v2a7 7 0 0 1-14 0v-2M12 19v3" />
              {!micOn && <path strokeLinecap="round" d="M3 3l18 18" />}
            </svg>
            {micOn ? 'Talking' : 'Talk'}
          </button>
        )}
        {hasMic && speechAvailable && (
          <button type="button" onClick={onSpeak} className={`${tool} ${toolState(false)}`}>
            <svg aria-hidden="true" className="w-[17px] h-[17px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            Speak a message
          </button>
        )}
        {!micOn && heldBy && <span className="col-span-2 text-[13px] text-attention">{describeHolder(heldBy)}</span>}
        {missed.length > 0 && (
          <span role="status" className="col-span-2 text-[13px] text-attention">{missed.join(' and ')} did not answer</span>
        )}
      </div>
      {page ? children : null}
    </div>
  )
}
